import React, { useState, useEffect } from 'react';
import middleware from "../../Api/Middleware";
import { BorderedDropDown } from '../../HouseStyle/Components/DropDowns';
import { BorderedTextInput } from '../../HouseStyle/Components/Inputs';
import ToastError from '../../Toasts/ToastError';
import ToastInfo from "../../Toasts/ToastInfo";
import { ButtonBlue, ButtonLightGrey } from '../../HouseStyle/Components/Buttons';
import Phonenumber from '../../Customer/Components/Phonenumber';

const Step1BContactInfo = ({ data, updateData, onNext, onPrev }) => {
  const [countries, setCountries] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [formData, setFormData] = useState({
    postal_code: data.postal_code || '',
    house_number: data.house_number || '',
    addition: data.addition || '',
    street_name: data.street_name || '',
    city: data.city || '',
    country: data.country || '',
    phone_number: data.phone_number || '',
    email: data.email || '',
    phone_number_country_code: data.phone_number_country_code || '31'
  });
  const [phoneNumbers, setPhoneNumbers] = useState([
    {
      country_phone_prefix: data.phone_number_country_code || "31",
      phone_number: data.phone_number || "",
    }
  ]);

  useEffect(() => {
    setFormData({
      postal_code: data.postal_code || '',
      house_number: data.house_number || '',
      addition: data.addition || '',
      street_name: data.street_name || '',
      city: data.city || '',
      country: data.country || '',
      phone_number: data.phone_number || '',
      email: data.email || '',
      phone_number_country_code: data.phone_number_country_code || '31'
    });
    setPhoneNumbers([
      {
        country_phone_prefix: data.phone_number_country_code || "31",
        phone_number: data.phone_number || "",
      }
    ]);
  }, [data]);

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const [fetchCountries] = await Promise.all([
          middleware.get(`/countryCodes`),
        ]);
        setCountries(fetchCountries.data.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountry();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    updateData({ [field]: value });
  };

  const isCountrySelected = !!formData.country;
  const countryValue = String(formData.country).toLowerCase();
  const selectedCountryObj = countries.find(
    c => String(c.country_code_id).toLowerCase() === countryValue ||
         String(c.country).toLowerCase() === countryValue
  );
  const isNetherlands = selectedCountryObj && (
    ["netherlands", "nederland"].includes(String(selectedCountryObj.country).toLowerCase()) ||
    String(selectedCountryObj.country_code_id).toLowerCase() === "nl"
  );

  useEffect(() => {
    if (isNetherlands) {
      const { postal_code, house_number } = formData;
      const postalCodeRegex = /^\s*[0-9]{4}\s*[A-Za-z]{2}\s*$/;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (postal_code) {
        const newDebounceTimer = setTimeout(() => {
          if (postalCodeRegex.test(postal_code)) {
            const fetchAdress = async () => {
              if (house_number) {
                try {
                  const response = await fetch(
                    `https://gratispostcodeapi.nl/api/v1/lookup/${postal_code}/${house_number}`
                  );
                  const result = await response.json();
                  if (result && result.postal_code) {
                    setFormData(prev => ({
                      ...prev,
                      street_name: result.street,
                      city: result.city,
                      addition:
                        (!prev.addition || prev.addition === "") && result.available_suffixes && result.available_suffixes.length > 0
                          ? result.available_suffixes[0]
                          : prev.addition,
                    }));
                    updateData({
                      street_name: result.street,
                      city: result.city,
                      addition:
                        (!formData.addition || formData.addition === "") && result.available_suffixes && result.available_suffixes.length > 0
                          ? result.available_suffixes[0]
                          : formData.addition,
                    });
                  } else {
                    ToastError("Geen adres gevonden voor deze postcode.");
                  }
                } catch (error) {
                  ToastError("Er is een fout opgetreden bij het ophalen de adress gegevens");
                }
              } else {
                ToastInfo("Voer het huisnummer in om automatisch de straat en stad in te vullen");
              }
            };
            fetchAdress();
          }
        }, 1000);
        setDebounceTimer(newDebounceTimer);
      }
      return () => clearTimeout(debounceTimer);
    }
  }, [formData.country, formData.postal_code, formData.house_number, isNetherlands, countries, formData.addition]);

  // Handler to update country code
  const setPhoneCode = (index, value) => {
    setPhoneNumbers(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, country_phone_prefix: value } : item
      )
    );
    handleInputChange('phone_number_country_code', value);
  };

  // Handler to update phone number (limit to 15 chars)
  const setPhoneNumber = (index, value) => {
    if (value.length <= 15) {
      setPhoneNumbers(prev =>
        prev.map((item, i) =>
          i === index ? { ...item, phone_number: value } : item
        )
      );
      handleInputChange('phone_number', value);
    }
  };

  const handleNext = () => {
    // Validate required fields
    const requiredFields = [
      { key: 'postal_code', label: 'Postcode' },
      { key: 'house_number', label: 'Huisnummer' },
      { key: 'street_name', label: 'Straat' },
      { key: 'city', label: 'Stad' },
      { key: 'country', label: 'Land' }
    ];
    const missingFields = requiredFields.filter(field => {
      const value = formData[field.key];
      return !value || String(value).trim() === '';
    });
    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(field => field.label).join(', ');
      ToastError(`Vul de volgende verplichte velden in: ${missingFieldNames}`);
      return;
    }
    // House number validation
    if (!formData.house_number || isNaN(Number(formData.house_number.trim()))) {
      ToastError('Huisnummer moet een geldig getal zijn');
      return;
    }
    // Email validation
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        ToastError('Vul een geldig e-mailadres in');
        return;
      }
    }
    // Phone number validation (accept any country code and number, allow spaces, E.164 style)
    const phoneNumberValue = phoneNumbers[0]?.phone_number || '';
    const countryPrefix = phoneNumbers[0]?.country_phone_prefix || "31";
    if (phoneNumberValue) {
      const cleanedNumber = phoneNumberValue.replace(/\s+/g, '');
      const fullNumber = `+${countryPrefix}${cleanedNumber}`;
      const phoneRegexGeneric = /^\+\d{1,4}\d{6,14}$/;
      if (!phoneRegexGeneric.test(fullNumber)) {
        ToastError('Vul een geldig telefoonnummer in (bijv. +32470123456 of +375 123 456 789)');
        return;
      }
      handleInputChange('phone_number', phoneNumberValue);
      handleInputChange('phone_number_country_code', countryPrefix);
    }
    onNext();
  };

  return (
    <div className="step-container">
      <div className="step-content-area">
        <div className="step-form">
          <h2 className="step-title">Contact & Adres</h2>
          <p className="required-note">Invulvelden met een (*) zijn verplicht om in te vullen</p>
                    <div className="row col-md-12 mt-3">
            <div className="col-md-6">
              <label>Algemeen telefoon nummer</label>
              {phoneNumbers.map((phoneNumber, index) => (
                <Phonenumber
                  key={index}
                  index={index}
                  phonenumber={phoneNumber}
                  setPhoneCode={(value) => setPhoneCode(index, value)}
                  handlePhoneNumberChange={(value) => setPhoneNumber(index, value)}
                  removePhoneNumberField={() => {}}
                  removable={false}
                />
              ))}
            </div>
            <div className="col-md-6">
              <label>
                Algemeen E-mail
                <span className="email-note">( Dit e-mail adres word gebruikt voor facturen en klanten e-mails )</span>
              </label>
              <BorderedTextInput
                placeholder="..."
                required={false}
                setInputValue={(value) => handleInputChange('email', value)}
                inputValue={formData.email}
              />
            </div>
          </div>
          <div className="divider"></div>
          <div className="row col-md-12 mt-3">
            <div className="col-md-3">
              <label>Land *</label>
              <BorderedDropDown
                size="s"
                placeholder="Kies een land"
                selectedValue={formData.country}
                setSelectedValue={(value) => handleInputChange('country', value)}
                options={
                  Array.isArray(countries) && countries.length > 0
                    ? (() => {
                        const nl = countries.find(c => c.country === "Netherlands");
                        const be = countries.find(c => c.country === "Belgium");
                        const de = countries.find(c => c.country === "Germany");
                        const rest = countries.filter(c =>
                          c.country !== "Netherlands" &&
                          c.country !== "Belgium" &&
                          c.country !== "Germany"
                        );
                        const divider = { key: 'divider', value: '', title: '', disabled: true, divider: true };
                        const ordered = [nl, be, de].filter(Boolean);
                        let optionsArr = ordered.map((country) => ({
                          key: country.country_code_id,
                          value: country.country_code_id,
                          title: country.country
                        }));
                        if (optionsArr.length === 3) optionsArr.push(divider);
                        optionsArr = optionsArr.concat(rest.map((country) => ({
                          key: country.country_code_id,
                          value: country.country_code_id,
                          title: country.country
                        })));
                        return optionsArr;
                      })()
                    : []
                }
              />
            </div>
          </div>
          <div className="row col-md-12 mt-3">
            <div className="col-md-6">
              <label>Postcode *</label>
              <BorderedTextInput
                placeholder="..."
                required={true}
                setInputValue={(value) => handleInputChange('postal_code', value)}
                inputValue={formData.postal_code}
                disabled={!isCountrySelected ? true : isNetherlands ? false : false}
              />
            </div>
            <div className="col-md-3">
              <label>Huisnummer *</label>
              <BorderedTextInput
                placeholder="..."
                required={true}
                setInputValue={(value) => handleInputChange('house_number', value)}
                inputValue={formData.house_number}
                disabled={!isCountrySelected ? true : isNetherlands ? false : false}
              />
            </div>
            <div className="col-md-3">
              <label>Toevoeging</label>
              <BorderedTextInput
                placeholder="..."
                required={false}
                setInputValue={(value) => handleInputChange('addition', value)}
                inputValue={formData.addition}
                disabled={!isCountrySelected}
              />
            </div>
          </div>
          <div className="row col-md-12 mt-3">
            <div className="col-md-6">
              <label>Straat *</label>
              <BorderedTextInput
                placeholder="..."
                required={true}
                setInputValue={(value) => handleInputChange('street_name', value)}
                inputValue={formData.street_name}
                disabled={!isCountrySelected ? true : isNetherlands ? true : false}
              />
            </div>
            <div className="col-md-6">
              <label>Stad *</label>
              <BorderedTextInput
                placeholder="..."
                required={true}
                setInputValue={(value) => handleInputChange('city', value)}
                inputValue={formData.city}
                disabled={!isCountrySelected ? true : isNetherlands ? true : false}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="form-actions">
        <ButtonLightGrey
            onClickFunction={onPrev}
            text="Terug"
        />
        <ButtonBlue
          onClickFunction={handleNext}
          text="Verder"
        />
      </div>
    </div>
  );
};

export default Step1BContactInfo;
